/**
 * Workforce Modeller Component
 * Cohort-based SSP cost modelling with Current vs 2026 comparison
 */

import React, { useState, useMemo } from 'react';
import {
  Plus,
  Trash2,
  RefreshCw,
  Users,
  Clock,
  PoundSterling,
  Calendar,
  AlertCircle,
  TrendingUp,
  ArrowRight,
  Info,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { formatCurrency, formatPercent, shiftsToProability, probabilityToShifts } from '../utils/sspCalculations';
import { LOWER_EARNINGS_LIMIT, SSP_WEEKLY_RATE } from '../data/constants';
import TooltipComponent from './Tooltip';
import { TOOLTIP_CONTENT } from '../data/tooltipContent.jsx';

export default function WorkforceModeller({
  cohorts,
  onAddCohort,
  onUpdateCohort,
  onRemoveCohort,
  onResetCohorts,
  pattern,
  onUpdateDistribution,
  onUpdateAvgLength,
  onUpdateEpisodes,
  distributionTotal,
  isValidDistribution,
  backfillOptions,
  onUpdateBackfillOption,
  results,
}) {
  const [expandedCohort, setExpandedCohort] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  const { cohortResults, totals } = results;
  
  // Chart data
  const comparisonChartData = cohortResults.map(c => ({
    name: c.cohort.length > 12 ? c.cohort.substring(0, 12) + '...' : c.cohort,
    current: Math.round(c.current.sspCost),
    new2026: Math.round(c.new2026.sspCost),
  }));
  
  const costBreakdownData = [
    { name: 'Current SSP', value: totals.currentSSPCost, color: '#6366f1' },
    { name: 'Additional 2026', value: totals.sspCostIncrease, color: '#f97316' },
  ].filter(d => d.value > 0);
  
  // Calculate impact drivers
  const shortTermAbsencePercent = pattern.distribution.oneDay + pattern.distribution.twoDay + pattern.distribution.threeDay;

  // Estimate cost breakdown
  const waitingDaysCost = useMemo(() => {
    // Approximate: Additional days paid × average daily rate
    const avgDailyRate = totals.currentSSPDays > 0
      ? totals.currentSSPCost / totals.currentSSPDays
      : 0;
    return totals.additionalDaysPaid * avgDailyRate * 0.6; // ~60% of increase from waiting days
  }, [totals]);

  const lelRemovalCost = totals.employeesBelowLEL > 0
    ? totals.sspCostIncrease - waitingDaysCost
    : 0;

  return (
    <div className="space-y-6">
      {/* Executive Summary Header */}
      <div className="bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-xl p-6 shadow-lg">
        <h2 className="text-2xl font-bold mb-2">Your SSP Impact Summary</h2>
        <p className="text-primary-100 text-sm">
          Projected annual cost comparison: Current rules vs April 2026 Employment Rights Act
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="stat-card-current">
          <p className="text-sm font-medium text-primary-600 mb-1">Current Rules</p>
          <p className="text-2xl font-bold text-primary-900 font-mono">
            {formatCurrency(totals.currentSSPCost, false)}
          </p>
          <p className="text-xs text-primary-600 mt-1">
            {totals.currentSSPDays.toFixed(0)} days paid
          </p>
        </div>
        
        <div className="stat-card-new">
          <p className="text-sm font-medium text-accent-600 mb-1">April 2026</p>
          <p className="text-2xl font-bold text-accent-900 font-mono">
            {formatCurrency(totals.newSSPCost, false)}
          </p>
          <p className="text-xs text-accent-600 mt-1">
            {totals.newSSPDays.toFixed(0)} days paid
          </p>
        </div>
        
        <div className="stat-card-increase">
          <p className="text-sm font-medium text-red-600 mb-1">SSP Increase</p>
          <p className="text-2xl font-bold text-red-900 font-mono">
            +{formatCurrency(totals.sspCostIncrease, false)}
          </p>
          <p className="text-xs text-red-600 mt-1">
            {formatPercent(totals.percentSSPIncrease)}
          </p>
        </div>
        
        <div className="stat-card-neutral">
          <p className="text-sm font-medium text-slate-600 mb-1">Per Employee</p>
          <p className="text-lg font-bold text-slate-900 font-mono">
            {formatCurrency(totals.currentCostPerEmployee)} → {formatCurrency(totals.newCostPerEmployee)}
          </p>
          <p className="text-xs text-slate-600 mt-1">
            {totals.employeeCount} employees total
          </p>
        </div>
      </div>
      
      {/* Key Insights */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="space-y-1 text-sm">
            <p className="font-medium text-amber-900">Key Impact Drivers</p>
            <ul className="text-amber-800 space-y-1">
              <li>• <strong>{(pattern.distribution.oneDay + pattern.distribution.twoDay + pattern.distribution.threeDay).toFixed(0)}%</strong> of absences are 1-3 days — currently £0, all payable from 2026</li>
              {totals.employeesBelowLEL > 0 && (
                <li>• <strong>{totals.employeesBelowLEL}</strong> employees below LEL gain SSP eligibility (at 80% of earnings)</li>
              )}
              <li>• SSP days paid increases by <strong>{(totals.additionalDaysPaid).toFixed(0)}</strong> days/year (+{((totals.newSSPDays / totals.currentSSPDays - 1) * 100).toFixed(0)}%)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Impact Driver Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Short-term Absences Impact */}
        <div className="card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <div className="card-body">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Short-term Absences</p>
                <p className="text-sm text-blue-700">1-3 day absences</p>
              </div>
              <Calendar className="w-8 h-8 text-blue-400" />
            </div>
            <div className="space-y-2">
              <div>
                <p className="text-3xl font-bold text-blue-900">
                  {shortTermAbsencePercent.toFixed(0)}%
                </p>
                <p className="text-xs text-blue-600">of all absences</p>
              </div>
              <div className="pt-2 border-t border-blue-200">
                <p className="text-xs text-blue-700 font-medium">Currently: £0 SSP</p>
                <p className="text-xs text-blue-700 font-medium">From 2026: Fully payable</p>
              </div>
            </div>
          </div>
        </div>

        {/* Employees Below LEL */}
        <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <div className="card-body">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs font-medium text-purple-600 uppercase tracking-wide mb-1">Newly Eligible</p>
                <p className="text-sm text-purple-700">Below earnings limit</p>
              </div>
              <Users className="w-8 h-8 text-purple-400" />
            </div>
            <div className="space-y-2">
              <div>
                <p className="text-3xl font-bold text-purple-900">
                  {totals.employeesBelowLEL}
                </p>
                <p className="text-xs text-purple-600">
                  employees ({totals.employeeCount > 0 ? ((totals.employeesBelowLEL / totals.employeeCount) * 100).toFixed(0) : 0}%)
                </p>
              </div>
              <div className="pt-2 border-t border-purple-200">
                <p className="text-xs text-purple-700 font-medium">Currently: Not eligible</p>
                <p className="text-xs text-purple-700 font-medium">From 2026: 80% of AWE</p>
              </div>
            </div>
          </div>
        </div>

        {/* Waiting Days Cost */}
        <div className="card bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <div className="card-body">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-xs font-medium text-orange-600 uppercase tracking-wide mb-1">Waiting Days Removal</p>
                <p className="text-sm text-orange-700">Day 1 payment impact</p>
              </div>
              <Clock className="w-8 h-8 text-orange-400" />
            </div>
            <div className="space-y-2">
              <div>
                <p className="text-3xl font-bold text-orange-900">
                  {formatCurrency(waitingDaysCost, false)}
                </p>
                <p className="text-xs text-orange-600">
                  ~{totals.sspCostIncrease > 0 ? ((waitingDaysCost / totals.sspCostIncrease) * 100).toFixed(0) : 0}% of increase
                </p>
              </div>
              <div className="pt-2 border-t border-orange-200">
                <p className="text-xs text-orange-700 font-medium">Currently: 3 waiting days</p>
                <p className="text-xs text-orange-700 font-medium">From 2026: 0 waiting days</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Configuration */}
        <div className="lg:col-span-2 space-y-6">
          {/* Employee Cohorts */}
          <div className="card">
            <div className="card-header flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-slate-400" />
                <h2 className="font-semibold text-slate-900">Employee Cohorts</h2>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={onResetCohorts} className="btn-ghost btn-xs" title="Reset to defaults">
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button onClick={() => onAddCohort()} className="btn-primary btn-sm">
                  <Plus className="w-4 h-4" />
                  Add Cohort
                </button>
              </div>
            </div>
            
            <div className="card-body space-y-4">
              {cohorts.map((cohort, index) => {
                const result = cohortResults[index];
                const weeklyEarnings = (cohort.contractedHours + cohort.avgPickupHours) * cohort.hourlyRate;
                const meetsLEL = weeklyEarnings >= LOWER_EARNINGS_LIMIT;
                const isExpanded = expandedCohort === cohort.id;
                
                return (
                  <div 
                    key={cohort.id} 
                    className={`border rounded-lg transition-all ${
                      isExpanded ? 'border-primary-300 bg-primary-50/30' : 'border-slate-200 bg-slate-50/50'
                    }`}
                  >
                    {/* Cohort Header */}
                    <div 
                      className="p-4 cursor-pointer"
                      onClick={() => setExpandedCohort(isExpanded ? null : cohort.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button className="text-slate-400 hover:text-slate-600">
                            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                          </button>
                          <div>
                            <input
                              type="text"
                              value={cohort.name}
                              onChange={(e) => onUpdateCohort(cohort.id, { name: e.target.value })}
                              onClick={(e) => e.stopPropagation()}
                              className="font-medium text-slate-900 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-primary-500 focus:outline-none"
                            />
                            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                              <span>{cohort.count} employees</span>
                              <span>•</span>
                              <span>{formatCurrency(weeklyEarnings)}/wk</span>
                              <span className={`px-1.5 py-0.5 rounded ${meetsLEL ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                {meetsLEL ? 'Above LEL' : 'Below LEL'}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-sm text-slate-500">SSP Impact</p>
                            <p className="font-semibold text-red-600 font-mono">
                              +{formatCurrency(result?.impact.additionalSSPCost || 0, false)}
                            </p>
                          </div>
                          {cohorts.length > 1 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onRemoveCohort(cohort.id);
                              }}
                              className="text-slate-400 hover:text-red-500 p-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-slate-200 pt-4 animate-fade-in">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Employees</label>
                            <input
                              type="number"
                              value={cohort.count}
                              onChange={(e) => onUpdateCohort(cohort.id, { count: parseInt(e.target.value) || 0 })}
                              className="input-field-compact w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Hourly Rate (£)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={cohort.hourlyRate}
                              onChange={(e) => onUpdateCohort(cohort.id, { hourlyRate: parseFloat(e.target.value) || 0 })}
                              className="input-field-compact w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Contracted Hrs/Wk</label>
                            <input
                              type="number"
                              value={cohort.contractedHours}
                              onChange={(e) => onUpdateCohort(cohort.id, { contractedHours: parseFloat(e.target.value) || 0 })}
                              className="input-field-compact w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Avg Pickup Hrs</label>
                            <input
                              type="number"
                              value={cohort.avgPickupHours}
                              onChange={(e) => onUpdateCohort(cohort.id, { avgPickupHours: parseFloat(e.target.value) || 0 })}
                              className="input-field-compact w-full"
                            />
                          </div>
                          <div>
                            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 mb-1">
                              Qualifying Days
                              <TooltipComponent content={TOOLTIP_CONTENT.qualifyingDays} />
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="7"
                              value={cohort.qualifyingDays}
                              onChange={(e) => onUpdateCohort(cohort.id, { qualifyingDays: parseInt(e.target.value) || 1 })}
                              className="input-field-compact w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Avg Shift Length</label>
                            <input
                              type="number"
                              step="0.5"
                              value={cohort.typicalShiftLength}
                              onChange={(e) => onUpdateCohort(cohort.id, { typicalShiftLength: parseFloat(e.target.value) || 0 })}
                              className="input-field-compact w-full"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">
                              Shifts/Week
                              <span className="text-slate-400 ml-1">(≈ {(cohort.rotaProbability * 7).toFixed(1)})</span>
                            </label>
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              max="7"
                              value={(cohort.rotaProbability * 7).toFixed(1)}
                              onChange={(e) => onUpdateCohort(cohort.id, { rotaProbability: shiftsToProability(parseFloat(e.target.value) || 0) })}
                              className="input-field-compact w-full"
                            />
                          </div>
                          <div>
                            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 mb-1">
                              Rota Probability
                              <span className="text-slate-400 ml-1">({(cohort.rotaProbability * 100).toFixed(0)}%)</span>
                              <TooltipComponent content={TOOLTIP_CONTENT.rotaProbability} />
                            </label>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.01"
                              value={cohort.rotaProbability}
                              onChange={(e) => onUpdateCohort(cohort.id, { rotaProbability: parseFloat(e.target.value) })}
                              className="w-full accent-primary-600"
                            />
                          </div>
                        </div>
                        
                        {/* Cohort Results */}
                        {result && (
                          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-3 gap-4 text-sm">
                            <div className="text-center p-2 bg-primary-50 rounded">
                              <p className="text-xs text-primary-600">Current SSP</p>
                              <p className="font-semibold text-primary-900 font-mono">{formatCurrency(result.current.sspCost)}</p>
                            </div>
                            <div className="text-center p-2 bg-accent-50 rounded">
                              <p className="text-xs text-accent-600">2026 SSP</p>
                              <p className="font-semibold text-accent-900 font-mono">{formatCurrency(result.new2026.sspCost)}</p>
                            </div>
                            <div className="text-center p-2 bg-red-50 rounded">
                              <p className="text-xs text-red-600">Increase</p>
                              <p className="font-semibold text-red-900 font-mono">+{formatCurrency(result.impact.additionalSSPCost)}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Sickness Pattern */}
          <div className="card">
            <div className="card-header flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-slate-400" />
                <h2 className="font-semibold text-slate-900">Sickness Patterns</h2>
              </div>
              <div className={`text-sm font-medium px-2 py-1 rounded ${
                isValidDistribution ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                Total: {distributionTotal.toFixed(1)}%
              </div>
            </div>
            
            <div className="card-body">
              <div className="mb-4">
                <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-2">
                  Average sickness episodes per employee per year
                  <TooltipComponent content={TOOLTIP_CONTENT.avgEpisodesPerYear} />
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={pattern.avgEpisodesPerEmployee}
                  onChange={(e) => onUpdateEpisodes(e.target.value)}
                  className="input-field-compact w-24"
                />
                <p className="mt-1 text-xs text-slate-500">UK average: ~2.5 episodes/year</p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { key: 'oneDay', label: '1 Day', days: 1, noSSP: true },
                  { key: 'twoDay', label: '2 Days', days: 2, noSSP: true },
                  { key: 'threeDay', label: '3 Days', days: 3, noSSP: true },
                  { key: 'fourToSeven', label: '4-7 Days', days: null, noSSP: false, avgKey: 'fourToSeven' },
                  { key: 'eightPlus', label: '8+ Days', days: null, noSSP: false, avgKey: 'eightPlus' },
                ].map(item => (
                  <div 
                    key={item.key}
                    className={`p-3 rounded-lg border ${
                      item.noSSP 
                        ? 'bg-red-50 border-red-200' 
                        : 'bg-green-50 border-green-200'
                    }`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className={`text-sm font-medium ${item.noSSP ? 'text-red-900' : 'text-green-900'}`}>
                        {item.label}
                      </span>
                      {item.noSSP && (
                        <span className="text-xs text-red-500 font-medium">No SSP*</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={pattern.distribution[item.key]}
                        onChange={(e) => onUpdateDistribution(item.key, e.target.value)}
                        className={`w-16 px-2 py-1 text-sm border rounded ${
                          item.noSSP ? 'border-red-300' : 'border-green-300'
                        }`}
                      />
                      <span className="text-sm text-slate-600">%</span>
                      {item.avgKey && (
                        <>
                          <span className="text-xs text-slate-400 ml-1">avg:</span>
                          <input
                            type="number"
                            value={pattern.avgLengths[item.avgKey]}
                            onChange={(e) => onUpdateAvgLength(item.avgKey, e.target.value)}
                            className="w-14 px-2 py-1 text-sm border border-green-300 rounded"
                          />
                          <span className="text-xs text-slate-500">d</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-slate-500">
                * Currently: 1-3 day absences don't qualify for SSP (waiting days). From April 2026, all absences paid from day 1.
              </p>
            </div>
          </div>
          
          {/* Backfill Options */}
          <div className="card">
            <button 
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="card-header flex justify-between items-center w-full text-left"
            >
              <div className="flex items-center gap-2">
                <PoundSterling className="w-5 h-5 text-slate-400" />
                <h2 className="font-semibold text-slate-900">Backfill Costs</h2>
              </div>
              {showAdvanced ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            
            {showAdvanced && (
              <div className="card-body animate-fade-in">
                <div className="flex items-center gap-4 mb-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={backfillOptions.includeBackfill}
                      onChange={(e) => onUpdateBackfillOption('includeBackfill', e.target.checked)}
                      className="rounded text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-slate-700">Include backfill costs in total</span>
                  </label>
                </div>
                
                {backfillOptions.includeBackfill && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Backfill Method</label>
                      <select
                        value={backfillOptions.method}
                        onChange={(e) => onUpdateBackfillOption('method', e.target.value)}
                        className="input-field"
                      >
                        <option value="same-rate">Same hourly rate</option>
                        <option value="overtime">Overtime premium</option>
                        <option value="agency">Agency rate</option>
                      </select>
                    </div>
                    
                    {backfillOptions.method === 'overtime' && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Overtime Premium</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.05"
                            value={backfillOptions.overtimePremium}
                            onChange={(e) => onUpdateBackfillOption('overtimePremium', parseFloat(e.target.value))}
                            className="input-field-compact w-20"
                          />
                          <span className="text-sm text-slate-500">× rate ({((backfillOptions.overtimePremium - 1) * 100).toFixed(0)}% extra)</span>
                        </div>
                      </div>
                    )}
                    
                    {backfillOptions.method === 'agency' && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Agency Markup</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            step="0.05"
                            value={backfillOptions.agencyMarkup}
                            onChange={(e) => onUpdateBackfillOption('agencyMarkup', parseFloat(e.target.value))}
                            className="input-field-compact w-20"
                          />
                          <span className="text-sm text-slate-500">× rate ({((backfillOptions.agencyMarkup - 1) * 100).toFixed(0)}% extra)</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Right: Charts & Results */}
        <div className="space-y-6">
          {/* Comparison Chart */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-slate-900">Cost by Cohort</h2>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={comparisonChartData} margin={{ top: 10, right: 10, left: 0, bottom: 70 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="name"
                    interval={0}
                    height={70}
                    tickLine={false}
                    tick={(props) => {
                      const { x, y, payload } = props;
                      const words = payload.value.split(' ');
                      return (
                        <g transform={`translate(${x},${y})`}>
                          {words.map((word, index) => (
                            <text
                              key={index}
                              x={0}
                              y={index * 12}
                              dy={8}
                              textAnchor="middle"
                              fill="#64748b"
                              fontSize={10}
                            >
                              {word}
                            </text>
                          ))}
                        </g>
                      );
                    }}
                  />
                  <YAxis tickFormatter={(v) => `£${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(value) => [formatCurrency(value, false), '']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Bar dataKey="current" name="Current" fill="#6366f1" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="new2026" name="2026" fill="#f97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Detailed Results Table */}
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-slate-900">Detailed Breakdown</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-2 text-xs font-semibold text-slate-600 uppercase">Cohort</th>
                    <th className="text-right py-2 px-1 text-xs font-semibold text-slate-600">#</th>
                    <th className="text-right py-2 px-1 text-xs font-semibold text-slate-600">Current</th>
                    <th className="text-right py-2 px-1 text-xs font-semibold text-slate-600">2026</th>
                    <th className="text-right py-2 px-2 text-xs font-semibold text-slate-600">+/-</th>
                  </tr>
                </thead>
                <tbody>
                  {cohortResults.map((c) => (
                    <tr key={c.cohort} className="border-b border-slate-100">
                      <td className="py-2 px-2 font-medium text-slate-900 text-sm">{c.cohort}</td>
                      <td className="py-2 px-1 text-right text-slate-700">{c.employeeCount}</td>
                      <td className="py-2 px-1 text-right font-mono text-xs text-primary-600 whitespace-nowrap">{formatCurrency(c.current.sspCost, false)}</td>
                      <td className="py-2 px-1 text-right font-mono text-xs text-accent-600 whitespace-nowrap">{formatCurrency(c.new2026.sspCost, false)}</td>
                      <td className="py-2 px-2 text-right font-mono text-xs text-red-600 whitespace-nowrap">+{formatCurrency(c.impact.additionalSSPCost, false)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 font-semibold">
                  <tr className="border-t-2 border-slate-300">
                    <td className="py-2 px-2 text-sm">Total</td>
                    <td className="py-2 px-1 text-right text-sm">{totals.employeeCount}</td>
                    <td className="py-2 px-1 text-right font-mono text-xs text-primary-700 whitespace-nowrap">{formatCurrency(totals.currentSSPCost, false)}</td>
                    <td className="py-2 px-1 text-right font-mono text-xs text-accent-700 whitespace-nowrap">{formatCurrency(totals.newSSPCost, false)}</td>
                    <td className="py-2 px-2 text-right font-mono text-xs text-red-700 whitespace-nowrap">+{formatCurrency(totals.sspCostIncrease, false)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          
          {/* Include backfill total if enabled */}
          {backfillOptions.includeBackfill && (
            <div className="card">
              <div className="card-body">
                <h3 className="font-semibold text-slate-900 mb-3">Total with Backfill</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Current (SSP + Backfill)</span>
                    <span className="font-mono font-medium">{formatCurrency(totals.currentTotalCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">2026 (SSP + Backfill)</span>
                    <span className="font-mono font-medium">{formatCurrency(totals.newTotalCost)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-slate-200">
                    <span className="text-slate-900 font-medium">Total Increase</span>
                    <span className="font-mono font-bold text-red-600">+{formatCurrency(totals.totalCostIncrease)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
